import { mergeAggregationGroups } from "@/components/controls/DataTable/groupSummaries";
import { useMemo } from "react";
import useAPI, {
  APIMutationLifecycle,
  useAPIMutation,
  useMultipleAPIs
} from "@/services/api/hook";
import { QueryClient, QueryKey, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { createUseFilteredEntitiesInfinite } from "@/services/api/infinite";
import {
  ComputedFields,
  ExtendedUseQueryResult,
  Filters,
  GenericAggregations,
  SearchQuery
} from "@/services/api/types";
import { APIFieldItem, APIFieldsResponse } from "@/pages/settings/types";
import {
  buildCompareFilterParams,
  type CompareFilterParams
} from "@/entities/dataTables/compareTo/compareExport";
import type { CompareToSelection } from "@/entities/dataTables/compareTo/types";
import {
  ContractData,
  ContractRevenueItem,
  ContractTableRow,
  ContractTableStatus,
  ContractUploadFiles,
  EqPaymentsBillingFrequency
} from "./types";
import { requestToRevenueItem } from "./revenueItemRequest";

export const CONTRACTS_TABLE_PAGE_SIZE = 50;

export interface ContractUpdatePayload {
  name?: string;
  dealId?: string;
  companyId?: string;
  partnerId?: string;
  productId?: string | null;
  currency?: string;
  startDate?: string | null;
  endDate?: string | null;
  renewalType?: string | number | null;
  aeOwner?: string | null;
  csmOwner?: string | null;
  signedOn?: string | null;
  revenueItems?: string[];
  arr?: number | null;
  arrLocal?: number | null;
  mrr?: number | null;
  mrrLocal?: number | null;
  annualContractValue?: number | null;
  annualContractValueLocal?: number | null;
  totalContractValue?: number | null;
  totalContractValueLocal?: number | null;
  carrLocal?: number | null;
  cmrrLocal?: number | null;
  // A positive number sets the rate by hand; null hands it back to the
  // automatic refresh. Never 0: the server would store it as a real rate.
  currentFxRate?: number | null;
  financialNote?: string | null;
  isDetachedFromDeal?: boolean | null;
  manuallyAddedPeople?: string[] | null;
  automaticallyAddedPeople?: string[] | null;
  manuallyAddedPartnerContacts?: string[] | null;
  automaticallyAddedPartnerContacts?: string[] | null;
  onboardingStartDate?: string | null;
  onboardingCompletedAt?: string | null;
  onboardingStageId?: string | null;
  uploadFiles?: ContractUploadFiles | null;
}

interface UpdateContractMutationData {
  pathParams: { contractId: string };
  data: ContractUpdatePayload;
}

export const useUpdateContract = (contractId: string | undefined) => {
  const queryClient = useQueryClient();

  return useAPIMutation<UpdateContractMutationData, ContractData>(
    "contracts/id",
    { method: "PUT" },
    () => {
      queryClient.invalidateQueries({
        queryKey: ["contracts/id", { pathParams: { contractId } }]
      });
    }
  );
};

export const useCreateContract = () => {
  const queryClient = useQueryClient();

  return useAPIMutation<ContractUpdatePayload, ContractData>(
    "contracts/create",
    { method: "POST" },
    () => {
      queryClient.invalidateQueries({
        queryKey: ["contracts/filter"]
      });
    },
    // A failed create may still have linked a contract to the deal; re-read it so the next add reuses it.
    (_error, variables) => {
      if (!variables.dealId) return;
      queryClient.invalidateQueries({
        queryKey: ["deals", { pathParams: { dealsIds: variables.dealId } }]
      });
    }
  );
};

export interface DuplicateRevenueItemPayload {
  serviceItemId: string | null;
  serviceItemName: string | null;
  serviceItemPricingId: string | null;
  serviceStartDate: string | null;
  serviceEndDate: string | null;
  autoRenewal: boolean;
  quantity: number | null;
  pricePerUnit: number | null;
  isDiscountPercentage: boolean | null;
  discountAmount: number | null;
  total: number | null;
  totalLocal: number | null;
  billingStartDate: string | null;
  billingEndDate: string | null;
  customBillingFrequency: number | null;
  customServiceDuration: number | null;
  customOptOutDate: string | null;
  customDuration: string | null;
  eqPaymentsNumOfPayments: number | null;
  eqPaymentsBillingFrequency: EqPaymentsBillingFrequency | null;
  itemOrder: number | null;
  scheduledPayments: Array<{
    paymentNumber: number;
    billingDate: string;
    amount: number;
    percentage: number;
  }>;
}

// Mirrors CarryOverOptions in contractsapi/messages.py (CRM-8463). Activities
// and tasks are never copied and have no toggle; omitted keys default to true.
// CRM-9072 removed the customFieldValues toggle from the UI; an activities
// toggle was considered but held back until the backend supports it.
export interface ContractCarryOverOptions {
  retentionWorkflowsData: boolean;
  onboardingStatusAndWorkflows: boolean;
  contractOwners: boolean;
  filesAndAttachments: boolean;
  peopleInContract: boolean;
}

export interface DuplicateContractPayload {
  name: string;
  productId: string | null;
  revenueItems: DuplicateRevenueItemPayload[];
  carryOver: ContractCarryOverOptions;
  acknowledgeOverlap: boolean;
}

interface DuplicateContractMutationData {
  pathParams: { contractId: string };
  data: DuplicateContractPayload;
}

export const useDuplicateContract = () => {
  const queryClient = useQueryClient();

  return useAPIMutation<DuplicateContractMutationData, ContractData>(
    "contracts/duplicate",
    { method: "POST" },
    () => {
      queryClient.invalidateQueries({
        queryKey: ["contracts/filter"]
      });
    }
  );
};

interface DeleteContractMutationData {
  pathParams: { contractId: string };
}

export const useDeleteContract = () => {
  const queryClient = useQueryClient();

  return useAPIMutation<DeleteContractMutationData, void>(
    "contracts/id",
    { method: "DELETE" },
    () => {
      queryClient.invalidateQueries({
        queryKey: ["contracts/filter"]
      });
    }
  );
};

export interface UpdateDealArrPayload {
  arrLocal: number;
  mrrLocal: number;
}

interface UpdateDealArrMutationData {
  pathParams: { dealId: string };
  data: UpdateDealArrPayload;
}

export const useUpdateDealArr = (contractId: string | undefined) => {
  const queryClient = useQueryClient();

  return useAPIMutation<UpdateDealArrMutationData, ContractData>(
    "contracts/dealArr",
    { method: "PUT" },
    () => {
      if (contractId) {
        queryClient.invalidateQueries({
          queryKey: ["contracts/id", { pathParams: { contractId } }]
        });
      }
    }
  );
};

export interface UpdateDealCurrencyPayload {
  currency: string;
}

interface UpdateDealCurrencyMutationData {
  pathParams: { dealId: string };
  data: UpdateDealCurrencyPayload;
}

export const useUpdateDealCurrency = (contractId: string | undefined) => {
  const queryClient = useQueryClient();

  return useAPIMutation<UpdateDealCurrencyMutationData, ContractData>(
    "contracts/dealCurrency",
    { method: "PUT" },
    () => {
      if (contractId) {
        queryClient.invalidateQueries({
          queryKey: ["contracts/id", { pathParams: { contractId } }]
        });
      }
    }
  );
};

export const useContract = (
  contractId: string | undefined,
  options: {
    enabled?: boolean;
    computed_fields?: ComputedFields;
    include_additionals?: boolean;
  } = {}
) => {
  const result = useAPI(
    "contracts/id",
    {
      pathParams: { contractId: contractId ?? "" },
      // Only request computed fields when a caller opts in — views that render
      // the CSM renewal-book / onboarding KPIs pass ComputedFields.ALL; the rest
      // keep the backend default so they don't pay for fields they don't use.
      queryParams: {
        ...(options.computed_fields && {
          computed_fields: options.computed_fields
        }),
        ...(options.include_additionals && {
          include_additionals: "true"
        })
      }
    },
    {
      enabled: options.enabled !== false && !!contractId
    }
  );

  return {
    ...result,
    data: result.data as ContractData | undefined
  };
};

export interface CreateRevenueItemRequest {
  serviceItemId: string;
  serviceItemName: string;
  serviceItemPricingId: string | null;
  serviceStartDate: string | null;
  serviceEndDate: string | null;
  autoRenewal: boolean;
  quantity: number | null;
  pricePerUnit: number | null;
  isDiscountPercentage: boolean;
  discountAmount: number;
  total?: number;
  totalLocal?: number;
  billingStartDate: string | null;
  billingEndDate: string | null;
  customBillingFrequency: number | null;
  customServiceDuration: number | null;
  customOptOutDate: string | null;
  eqPaymentsNumOfPayments: number | null;
  eqPaymentsBillingFrequency: EqPaymentsBillingFrequency | null;
  // Sent on every create/update so switching a line back to equal payments
  // clears the rows the backend still holds; the backend replaces the whole
  // set when the key is present and leaves it untouched when it is absent.
  scheduledPayments: ScheduledPaymentInput[];
  // Blank or null clears the note; omitted leaves it as it is.
  note?: string | null;
}

export interface ScheduledPaymentInput {
  paymentNumber: number;
  billingDate: string;
  amount: number | null;
  percentage: number | null;
}

interface CreateRevenueItemMutationData {
  pathParams: { contractId: string };
  data: CreateRevenueItemRequest;
}

const REVENUE_ITEM_MUTATIONS = "contracts/revenueItems";

// A row added on screen before the server has assigned its id. It can't be
// edited or deleted until the refetch replaces it with the saved row.
const PENDING_REVENUE_ITEM_ID_PREFIX = "pending-";

export const isPendingRevenueItem = (item: Pick<ContractRevenueItem, "id">) =>
  item.id.startsWith(PENDING_REVENUE_ITEM_ID_PREFIX);

type ContractCacheSnapshot = [QueryKey, ContractData | undefined][];

type RevenueItemMutationVariables = { pathParams: { contractId: string } };

interface OptimisticRevenueItemContext {
  snapshot: ContractCacheSnapshot;
  // The id the added row carries on screen until the refetch replaces it.
  pendingId: string;
}

const contractDetailKey = (contractId: string) => [
  "contracts/id",
  { pathParams: { contractId } }
];

const contractBundlesKey = (contractId: string) => [
  "contracts/bundles/list",
  { pathParams: { contractId } }
];

// Contracts that already have a refetch check waiting for the next task.
const scheduledRefetchChecks = new WeakMap<QueryClient, Set<string>>();

// Refetches the contract and deals once every save on the contract's services
// has settled. Refetching while another save is still in flight would drop
// that save's change from the screen until it lands, so the last one to
// settle refetches for all of them.
const useRefetchAfterRevenueItemSaves = () => {
  const queryClient = useQueryClient();

  return ({ pathParams: { contractId } }: RevenueItemMutationVariables) => {
    const scheduled =
      scheduledRefetchChecks.get(queryClient) ?? new Set<string>();
    scheduledRefetchChecks.set(queryClient, scheduled);
    // Saves settling together share one check, so they refetch once.
    if (scheduled.has(contractId)) return;
    scheduled.add(contractId);
    // onSettled runs before the mutation leaves the pending state, so two
    // saves settling together would each still count the other. Checking on
    // the next task lets both finish settling first.
    setTimeout(() => {
      scheduled.delete(contractId);
      const inFlight = queryClient.isMutating({
        predicate: (mutation) =>
          mutation.options.mutationKey?.[0] === REVENUE_ITEM_MUTATIONS &&
          (mutation.state.variables as RevenueItemMutationVariables | undefined)
            ?.pathParams?.contractId === contractId
      });
      if (inFlight > 0) return;
      queryClient.invalidateQueries({
        queryKey: contractDetailKey(contractId)
      });
      // A line saved, removed or moved can change its bundle, or remove it.
      queryClient.invalidateQueries({
        queryKey: contractBundlesKey(contractId)
      });
      // Saving a service re-derives the deal's financials as well.
      queryClient.invalidateQueries({ queryKey: ["deals"] });
    }, 0);
  };
};

// Shows a service change on every cached copy of the contract as soon as it is
// submitted, undoes it if the save fails, and refetches once the server has
// answered. Derived totals (ARR, TCV, ...) are computed server-side and update
// with that refetch.
//
// A failed save undoes only its own change (`revert`, given the rows as they
// were before it) rather than restoring the whole snapshot, which would also
// erase any other save on the contract that started meanwhile.
const useOptimisticRevenueItems = <
  TRequest extends RevenueItemMutationVariables,
  TResponse
>(
  patch: (
    items: ContractRevenueItem[],
    variables: TRequest,
    pendingId: string
  ) => ContractRevenueItem[],
  revert: (
    items: ContractRevenueItem[],
    itemsBefore: ContractRevenueItem[],
    variables: TRequest,
    pendingId: string
  ) => ContractRevenueItem[],
  { toastOnError = true }: { toastOnError?: boolean } = {}
): APIMutationLifecycle<TResponse, TRequest, OptimisticRevenueItemContext> => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const refetchAfterSaves = useRefetchAfterRevenueItemSaves();

  return {
    mutationKey: [REVENUE_ITEM_MUTATIONS],
    onMutate: async (variables) => {
      const queryKey = contractDetailKey(variables.pathParams.contractId);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueriesData<ContractData>({ queryKey });
      const pendingId = `${PENDING_REVENUE_ITEM_ID_PREFIX}${crypto.randomUUID()}`;
      queryClient.setQueriesData<ContractData>({ queryKey }, (contract) =>
        contract?.revenueItems
          ? {
              ...contract,
              revenueItems: patch(contract.revenueItems, variables, pendingId)
            }
          : contract
      );
      return { snapshot, pendingId };
    },
    onError: (_error, variables, context) => {
      context?.snapshot.forEach(([queryKey, before]) =>
        queryClient.setQueryData<ContractData>(queryKey, (contract) =>
          contract?.revenueItems && before?.revenueItems
            ? {
                ...contract,
                revenueItems: revert(
                  contract.revenueItems,
                  before.revenueItems,
                  variables,
                  context.pendingId
                )
              }
            : contract
        )
      );
      if (toastOnError) toast.error(t("contractTerms.serviceSaveFailed"));
    },
    onSettled: (_data, _error, variables) => refetchAfterSaves(variables)
  };
};

// Puts one row back to how it was before a failed edit or delete, in place if
// it is still on screen and at its old position if it is not.
const restoreRevenueItem = (
  items: ContractRevenueItem[],
  itemsBefore: ContractRevenueItem[],
  revenueItemId: string
) => {
  const index = itemsBefore.findIndex((item) => item.id === revenueItemId);
  if (index === -1) return items;
  const saved = itemsBefore[index];
  return items.some((item) => item.id === revenueItemId)
    ? items.map((item) => (item.id === revenueItemId ? saved : item))
    : [...items.slice(0, index), saved, ...items.slice(index)];
};

export const useCreateRevenueItem = ({
  toastOnError
}: { toastOnError?: boolean } = {}) =>
  useAPIMutation<
    CreateRevenueItemMutationData,
    ContractRevenueItem,
    OptimisticRevenueItemContext
  >(
    "contracts/revenueItems/create",
    { method: "POST" },
    undefined,
    undefined,
    useOptimisticRevenueItems(
      (items, { pathParams, data }, pendingId) => [
        ...items,
        requestToRevenueItem(data, pendingId, pathParams.contractId)
      ],
      (items, _itemsBefore, _variables, pendingId) =>
        items.filter((item) => item.id !== pendingId),
      { toastOnError }
    )
  );

export type UpdateRevenueItemRequest = CreateRevenueItemRequest;

interface UpdateRevenueItemMutationData {
  pathParams: { contractId: string; revenueItemId: string };
  data: UpdateRevenueItemRequest;
}

export const useUpdateRevenueItem = () =>
  useAPIMutation<
    UpdateRevenueItemMutationData,
    ContractRevenueItem,
    OptimisticRevenueItemContext
  >(
    "contracts/revenueItems/update",
    { method: "PUT" },
    undefined,
    undefined,
    useOptimisticRevenueItems(
      (items, { pathParams, data }) =>
        items.map((item) =>
          item.id === pathParams.revenueItemId
            ? {
                ...requestToRevenueItem(data, item.id, item.contractId),
                // Kept from the saved row until the refetch: the request only
                // carries the terms-currency total, and these are server-derived.
                total: data.total ?? item.total,
                createdAt: item.createdAt,
                itemOrder: item.itemOrder,
                periodIndex: item.periodIndex,
                previousPeriods: item.previousPeriods,
                status: item.status,
                durationDisplay: item.durationDisplay,
                customDuration: item.customDuration,
                // A request without the key leaves the stored note alone.
                note: data.note !== undefined ? data.note : item.note,
                noteUpdatedBy: item.noteUpdatedBy,
                noteUpdatedAt: item.noteUpdatedAt
              }
            : item
        ),
      (items, itemsBefore, { pathParams }) =>
        restoreRevenueItem(items, itemsBefore, pathParams.revenueItemId)
    )
  );

interface UpdateRevenueItemNoteMutationData {
  pathParams: { contractId: string; revenueItemId: string };
  data: { note: string | null };
}

// A body carrying only `note` writes just the note and its author: no version,
// no recompute. Keyed with the service saves so neither refetches over the
// other's change; the Services table reads the same rows, so it is refetched
// too. The popover reports its own failure, so no generic toast.
export const useUpdateRevenueItemNote = () => {
  const queryClient = useQueryClient();

  return useAPIMutation<
    UpdateRevenueItemNoteMutationData,
    ContractRevenueItem,
    OptimisticRevenueItemContext
  >(
    "contracts/revenueItems/update",
    { method: "PUT" },
    () => {
      queryClient.invalidateQueries({
        queryKey: ["contracts/serviceItems/filter"]
      });
    },
    undefined,
    useOptimisticRevenueItems(
      (items, { pathParams, data }) =>
        items.map((item) =>
          item.id === pathParams.revenueItemId
            ? { ...item, note: data.note }
            : item
        ),
      (items, itemsBefore, { pathParams }) =>
        restoreRevenueItem(items, itemsBefore, pathParams.revenueItemId),
      { toastOnError: false }
    )
  );
};

interface DeleteRevenueItemMutationData {
  pathParams: { contractId: string; revenueItemId: string };
}

export const useDeleteRevenueItem = () =>
  useAPIMutation<
    DeleteRevenueItemMutationData,
    void,
    OptimisticRevenueItemContext
  >(
    "contracts/revenueItems/delete",
    { method: "DELETE" },
    undefined,
    undefined,
    useOptimisticRevenueItems(
      (items, { pathParams }) =>
        items.filter((item) => item.id !== pathParams.revenueItemId),
      (items, itemsBefore, { pathParams }) =>
        restoreRevenueItem(items, itemsBefore, pathParams.revenueItemId)
    )
  );

export type RevenueItemsOrderPayload = {
  items: Record<number, string>;
};

interface UpdateRevenueItemsOrderMutationData {
  pathParams: { contractId: string };
  data: RevenueItemsOrderPayload;
}

// Keyed with the service saves so neither refetches over the other's change.
export const useUpdateRevenueItemsOrder = () => {
  const refetchAfterSaves = useRefetchAfterRevenueItemSaves();

  return useAPIMutation<UpdateRevenueItemsOrderMutationData, void>(
    "contracts/revenueItems/order",
    { method: "PUT" },
    undefined,
    undefined,
    {
      mutationKey: [REVENUE_ITEM_MUTATIONS],
      onSettled: (_data, _error, variables) => refetchAfterSaves(variables)
    }
  );
};

interface DeleteAllRevenueItemsMutationData {
  pathParams: { contractId: string };
}

// Keyed with the service saves so neither refetches over the other's change.
export const useDeleteAllRevenueItems = () => {
  const refetchAfterSaves = useRefetchAfterRevenueItemSaves();

  return useAPIMutation<DeleteAllRevenueItemsMutationData, void>(
    "contracts/revenueItems/deleteAll",
    { method: "DELETE" },
    undefined,
    undefined,
    {
      mutationKey: [REVENUE_ITEM_MUTATIONS],
      onSettled: (_data, _error, variables) => refetchAfterSaves(variables)
    }
  );
};

// One of the bundle's configured services, with the quantity and term it
// starts with when checked.
export interface ContractBundleItem {
  id: string;
  serviceItemId: string;
  serviceItemPricingId: string;
  quantity: number;
  itemOrder: number | null;
}

// A Settings bundle copied onto a contract; its lines are ordinary revenue items.
export interface ContractBundle {
  id: string;
  contractId: string;
  dealId: string | null;
  sourceBundleId: string | null;
  name: string;
  description: string | null;
  servicesStartDate: string;
  // The bundle's own discount: true for a percentage, false for an amount in
  // the contract's currency, null with none. Optional, as the other readers
  // and their fixtures don't set it.
  isDiscountPercentage?: boolean | null;
  discountAmount?: number | null;
  itemOrder?: number | null;
  items?: ContractBundleItem[];
  // The bundle's lines, in display order.
  revenueItemIds: string[];
}

// The bundle's own discount on a write. A null amount removes it; leaving both
// out keeps the stored one, or, when adding, takes the Settings default.
export interface BundleDiscountRequest {
  isDiscountPercentage?: boolean | null;
  discountAmount?: number | null;
}

// The contract's bundles; the table groups their lines under them.
export const useContractBundles = (
  contractId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const result = useAPI(
    "contracts/bundles/list",
    { pathParams: { contractId: contractId ?? "" } },
    { enabled: enabled && !!contractId }
  );

  return {
    ...result,
    data: result.data as { bundles: ContractBundle[] } | undefined
  };
};

export interface UpdateBundleLine extends Partial<CreateRevenueItemRequest> {
  // The existing line's id; omitted for a service checked in the drawer.
  id?: string;
  serviceItemId: string;
}

export interface UpdateBundleRequest extends BundleDiscountRequest {
  servicesStartDate: string;
  // The full set: a line left out is deleted.
  lines: UpdateBundleLine[];
}

interface UpdateBundleMutationData {
  pathParams: { bundleId: string; contractId: string };
  data: UpdateBundleRequest;
}

// Keyed with the service saves so neither refetches over the other's change.
export const useUpdateContractBundle = () => {
  const refetchAfterSaves = useRefetchAfterRevenueItemSaves();

  return useAPIMutation<UpdateBundleMutationData, ContractBundle>(
    "contracts/bundles/update",
    { method: "PUT" },
    undefined,
    undefined,
    {
      mutationKey: [REVENUE_ITEM_MUTATIONS],
      onSettled: (_data, _error, variables) => refetchAfterSaves(variables)
    }
  );
};

interface DeleteBundleMutationData {
  pathParams: { bundleId: string; contractId: string };
}

// Removes the bundle and all of its lines.
export const useDeleteContractBundle = () => {
  const refetchAfterSaves = useRefetchAfterRevenueItemSaves();

  return useAPIMutation<DeleteBundleMutationData, void>(
    "contracts/bundles/delete",
    { method: "DELETE" },
    undefined,
    undefined,
    {
      mutationKey: [REVENUE_ITEM_MUTATIONS],
      onSettled: (_data, _error, variables) => refetchAfterSaves(variables)
    }
  );
};

export interface AddBundleRequest extends BundleDiscountRequest {
  sourceBundleId: string;
  servicesStartDate: string;
  // One per checked service, built like a standalone line.
  lines: CreateRevenueItemRequest[];
}

interface AddBundleToContractMutationData {
  pathParams: { contractId: string };
  data: AddBundleRequest;
}

// Keyed with the service saves so neither refetches over the other's change.
export const useAddBundleToContract = () => {
  const refetchAfterSaves = useRefetchAfterRevenueItemSaves();

  return useAPIMutation<AddBundleToContractMutationData, ContractBundle>(
    "contracts/bundles/addToContract",
    { method: "POST" },
    undefined,
    undefined,
    {
      mutationKey: [REVENUE_ITEM_MUTATIONS],
      onSettled: (_data, _error, variables) => refetchAfterSaves(variables)
    }
  );
};

interface AddBundleToDealMutationData {
  pathParams: { dealId: string };
  data: AddBundleRequest;
}

// Creates the deal's contract when it has none, so the contract is known only from the answer.
export const useAddBundleToDeal = () => {
  const queryClient = useQueryClient();

  return useAPIMutation<AddBundleToDealMutationData, ContractBundle>(
    "contracts/bundles/addToDeal",
    { method: "POST" },
    (bundle) => {
      queryClient.invalidateQueries({
        queryKey: contractDetailKey(bundle.contractId)
      });
      queryClient.invalidateQueries({
        queryKey: contractBundlesKey(bundle.contractId)
      });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
    }
  );
};

export const {
  useFilteredEntitiesInfinite: useFilteredContractsInfinite,
  useResetFilteredEntitiesInfinite: useResetFilteredContracts
} = createUseFilteredEntitiesInfinite<ContractTableRow>(
  "contracts",
  CONTRACTS_TABLE_PAGE_SIZE,
  {
    responseKey: "contracts",
    extraQueryParams: { include_additionals: "false" },
    defaultAggregations: { groupBy: "all" }
  }
);

export interface ContractsAggregations {
  totalCount: number;
  totalContractValue: number;
  totalAnnualContractValue: number;
  totalArr: number;
  totalMrr: number;
  averageArr: number;
  averageMrr: number;
  totalCarr: number;
  totalCmrr: number;
  /**
   * The same filtered set's ARR/MRR as they stood at the Compare-to period
   * start, served alongside the totals above whenever the read carried a
   * period. Null, never 0, in all three cases that are not a
   * figure: no period was requested, the block is absent, or no contract in
   * the filter had a record that far back — a real total of zero and "nothing
   * to total" are different answers, and the tile is withheld for the second.
   */
  asOfArr: number | null;
  asOfMrr: number | null;
}

interface ContractsAggregationField {
  name: string;
  aggregations: { sum?: number | null; avg?: number | null };
}

/**
 * The Compare-to block the backend attaches to the aggregations when the
 * request named a period. Its keys are camelCased server-side by hand — the
 * rest of the aggregation payload is snake_case — so they are read as spelled.
 *
 * A sibling of `data`, not part of a group: it describes the whole filtered
 * set once, so it is read off the response root and never merged per group.
 */
interface ContractsCompareToAggregation {
  asOf?: string;
  contractCount?: number;
  totals?: Record<string, number | null>;
}

const getFieldSum = (
  fields: ContractsAggregationField[] | undefined,
  name: string
): number => fields?.find((f) => f.name === name)?.aggregations?.sum ?? 0;

const getFieldAvg = (
  fields: ContractsAggregationField[] | undefined,
  name: string
): number => fields?.find((f) => f.name === name)?.aggregations?.avg ?? 0;

/**
 * One as-of total, or null. Guarded rather than trusted: a non-numeric value
 * would otherwise reach the subtraction and render the tile as `NaN`.
 */
const getAsOfTotal = (
  compareTo: ContractsCompareToAggregation | undefined,
  metric: string
): number | null => {
  const value = compareTo?.totals?.[metric];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

export const parseContractsAggregations = (
  raw: Record<string, unknown>
): ContractsAggregations => {
  // The response is grouped whenever the table is, so the whole-set figures
  // come from every group merged back together, not from the first one.
  const group = mergeAggregationGroups(
    raw as unknown as GenericAggregations<string>
  ) as
    | { total_count?: number; fields?: ContractsAggregationField[] }
    | undefined;
  const compareTo = (raw as { compareTo?: ContractsCompareToAggregation })
    ?.compareTo;
  return {
    totalCount: group?.total_count ?? 0,
    totalContractValue: getFieldSum(group?.fields, "totalContractValue"),
    totalAnnualContractValue: getFieldSum(group?.fields, "annualContractValue"),
    totalArr: getFieldSum(group?.fields, "arr"),
    totalMrr: getFieldSum(group?.fields, "mrr"),
    averageArr: getFieldAvg(group?.fields, "arr"),
    averageMrr: getFieldAvg(group?.fields, "mrr"),
    totalCarr: getFieldSum(group?.fields, "carr"),
    totalCmrr: getFieldSum(group?.fields, "cmrr"),
    asOfArr: getAsOfTotal(compareTo, "arr"),
    asOfMrr: getAsOfTotal(compareTo, "mrr")
  };
};

const useNoopContractsStore = () => ({ addEntity: () => {} });

// Drafts are never listed, whatever status the caller filters on: DRAFT is
// dropped from a selection and added to an exclusion. A selection left empty
// once DRAFT is dropped narrows nothing, so it falls back to the exclusion.
const excludeDraftStatus = (status: unknown) => {
  if (Array.isArray(status)) {
    const selected = status.filter(
      (value) => value !== ContractTableStatus.DRAFT
    );
    return selected.length ? selected : { not_in: [ContractTableStatus.DRAFT] };
  }
  if (
    status !== null &&
    typeof status === "object" &&
    "not_in" in status &&
    Array.isArray(status.not_in)
  ) {
    return status.not_in.includes(ContractTableStatus.DRAFT)
      ? status
      : { not_in: [...status.not_in, ContractTableStatus.DRAFT] };
  }
  return { not_in: [ContractTableStatus.DRAFT] };
};

export const useFilteredContracts = (
  sortField?: string,
  sortAsc?: boolean,
  filters: Filters = {},
  searchTerm = "",
  enabled = true,
  includeDrafts = false,
  groupedBy?: string,
  compareTo?: CompareToSelection
) => {
  const search: SearchQuery | undefined = searchTerm
    ? { term: searchTerm }
    : undefined;

  // The Compare-to period rides on this read rather than a companion call of
  // its own: the as-of total has to cover the very set these filters select,
  // and a second request carrying the filters again is how the two drift apart
  // (the backend makes the same argument for its own read). Omitted entirely
  // when no period is selected, so a table without Compare-to sends and
  // receives exactly what it does today.
  const compareBodyParams = useMemo<CompareFilterParams | undefined>(
    () => buildCompareFilterParams(compareTo),
    [compareTo]
  );

  const effectiveFilters: Filters = useMemo(
    () =>
      includeDrafts
        ? filters
        : { ...filters, status: excludeDraftStatus(filters.status) },
    [filters, includeDrafts]
  );

  const result = useFilteredContractsInfinite(useNoopContractsStore, {
    filters: effectiveFilters,
    search,
    sortField,
    sortAsc,
    enabled,
    groupedBy: groupedBy as never,
    computed_fields: ComputedFields.MINIMAL,
    extraBodyParams: compareBodyParams
  });

  const aggregations = parseContractsAggregations(result.aggregations);

  return {
    contracts: result.entities,
    total: aggregations.totalCount || result.total,
    aggregations,
    /** Grouped as requested — one entry per distinct value of `groupedBy`. */
    rawAggregations: result.aggregations as unknown as
      | GenericAggregations<string>
      | undefined,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: result.fetchNextPage,
    isLoading: result.isLoading,
    isAggregationsLoading: result.isAggregationsLoading,
    isAggregationsError: result.isAggregationsError,
    isError: result.isError,
    refetch: result.refetch,
    effectiveFilters
  };
};

export const useContractFields =
  (): ExtendedUseQueryResult<APIFieldsResponse> => {
    const result = useAPI("settings/contracts/fields");

    const filteredData = useMemo(() => {
      return result.data
        ? {
            ...result.data,
            fields:
              result.data.fields?.filter(
                (field: APIFieldItem) => !field.isDeleted
              ) || []
          }
        : result.data;
    }, [result.data]);

    return useMemo(
      () => ({ ...result, data: filteredData }),
      [result, filteredData]
    );
  };

// Per-item response value serialized to/from the API `dict[str, Any]` shape.
// We wrap every value type under "value" to match the deals questionnaire convention.
export type WorkflowResponseDict = { value: unknown } | null;

export const serializeResponse = (value: unknown): WorkflowResponseDict => {
  if (value === undefined || value === null) return null;
  return { value };
};

export const deserializeResponse = (
  responseDict: WorkflowResponseDict
): unknown => {
  if (!responseDict) return undefined;
  return responseDict.value;
};

export type WorkflowResponsesMap = Record<string, WorkflowResponseDict>;

export const deserializeResponses = (
  map: WorkflowResponsesMap | undefined
): Record<string, unknown> => {
  if (!map) return {};
  const result: Record<string, unknown> = {};
  Object.entries(map).forEach(([id, dict]) => {
    result[id] = deserializeResponse(dict);
  });
  return result;
};

// A pending Dreamer value next to an answer a person entered. `value` is in the
// stored response format, so it reads as text through `responseValueText`.
export interface DreamerSuggestionDetails {
  value: unknown;
  citation: string | null;
  sourceActivityId: string | null;
}

export interface WorkflowItemResponseDetails {
  lastEditedBy: string | null;
  citation: string | null;
  activityId: string | null;
  dreamerSuggestion: DreamerSuggestionDetails | null;
}

export interface WorkflowResponsesResult {
  responses: WorkflowResponsesMap;
  scoreStatus: number | null;
  scoreStatusReason: string | null;
  // Provenance per answered item, keyed by item id.
  details?: Record<string, WorkflowItemResponseDetails>;
}

export const useWorkflowResponses = (
  contractId: string | undefined,
  workflowId: string | undefined
) => {
  const result = useAPI(
    "contracts/workflowResponses",
    {
      pathParams: { contractId: contractId ?? "", workflowId: workflowId ?? "" }
    },
    { enabled: !!contractId && !!workflowId }
  );
  return {
    ...result,
    data: result.data as WorkflowResponsesResult | undefined
  };
};

interface SaveWorkflowResponsesMutationData {
  pathParams: { contractId: string; workflowId: string };
  data: { responses: WorkflowResponsesMap };
}

export const usePrefetchAllWorkflowResponses = (
  contractId: string | undefined,
  workflowIds: string[]
) => {
  const configs = useMemo(
    () =>
      contractId
        ? workflowIds.map((workflowId) => ({
            endpoint: "contracts/workflowResponses" as const,
            options: { pathParams: { contractId, workflowId } }
          }))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contractId, workflowIds.join(",")]
  );
  useMultipleAPIs(configs);
};

export interface BlockedWorkflow {
  workflowId: string;
  workflowName: string | null;
  scoreStatus: number | null;
  scoreStatusReason: string | null;
  missingRequired: number;
}

export interface MoveOnboardingStageResult {
  contractId: string;
  onboardingStageId: string | null;
}

interface MoveOnboardingStageMutationData {
  pathParams: { contractId: string; stageId: string };
  data: Record<string, never>;
}

export const useMoveOnboardingStage = () =>
  useAPIMutation<MoveOnboardingStageMutationData, MoveOnboardingStageResult>(
    "contracts/onboardingStage",
    { method: "PUT" }
  );

export type WorkflowSuggestionAction = "approve" | "dismiss";

interface ResolveWorkflowItemSuggestionMutationData {
  pathParams: { contractId: string; itemId: string };
  data: { action: WorkflowSuggestionAction };
}

// Accept or dismiss Dreamer's suggestion on one item, then refetch the
// contract's workflow responses so the value, provenance and score update.
export const useResolveWorkflowItemSuggestion = () => {
  const queryClient = useQueryClient();
  return useAPIMutation<ResolveWorkflowItemSuggestionMutationData, unknown>(
    "contracts/workflowItemResolve",
    { method: "POST" },
    // Returned so callers' onSuccess runs only once the refetched responses are cached.
    (_result, variables) =>
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [endpoint, options] = query.queryKey as [
            string,
            { pathParams?: { contractId?: string } }?
          ];
          return (
            endpoint === "contracts/workflowResponses" &&
            options?.pathParams?.contractId === variables.pathParams.contractId
          );
        }
      })
  );
};

export const useSaveWorkflowResponses = () => {
  const queryClient = useQueryClient();
  return useAPIMutation<
    SaveWorkflowResponsesMutationData,
    WorkflowResponsesResult
  >(
    "contracts/workflowResponses",
    { method: "POST" },
    (
      result: WorkflowResponsesResult,
      variables: SaveWorkflowResponsesMutationData
    ) => {
      const queryKey = [
        "contracts/workflowResponses",
        { pathParams: variables.pathParams }
      ];
      queryClient.setQueryData(queryKey, result);
      queryClient.invalidateQueries({ queryKey });
    }
  );
};
